'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import { Logo } from '@/components/atoms/Logo/Logo';
import { ComponentContainer } from '@/components/atoms/ComponentContainer/ComponentContainer';
import { TwoColContainer } from '@/components/atoms/TwoColContainer/TwoColContainer';
import { CustomLink } from '@/components/atoms/CustomLink/CustomLink';
import { BurgerTwoLines } from '@/components/atoms/AnimatedIcons/BurgerTwoLines/BurgerTwoLines';
import { Button } from '@/components/atoms/Button/Button';
import { useScrollThreshold } from '@/hooks/useScrollThreshold';
import { allowPageScroll, blockPageScroll } from '@/utils/storeSetters';
import { usePageStore } from '@/store/usePageStore';
import { usePathname } from 'next/navigation';
import { isHomePage } from '@/utils/page';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import * as Styled from './Header.styled';

interface IMenu {
  text: string;
  href: string;
  isDynamicTranslate?: boolean;
};

interface IHeaderProps {
  navLeft: IMenu[];
  navRight: IMenu[];
}

export const Header: React.FC<IHeaderProps> = ({
  navLeft,
  navRight
}: IHeaderProps) => {
  const locale = usePageStore((s) => s.locale);
  const isMobileMenuOpen = usePageStore((s) => s.isMobileMenuOpen);
  const setIsMobileMenuOpen = usePageStore((s) => s.setIsMobileMenuOpen);
  const setIsPageLoadFromMobileHeader = usePageStore(
    (s) => s.setIsPageLoadFromMobileHeader
  );
  const setPageTransitionDuration = usePageStore(
    (s) => s.setPageTransitionDuration
  );
  const currentSlugTranslations = usePageStore(
    (s) => s.currentSlugTranslations
  );
  const defaultPageTransitionDuration = usePageStore(
    (s) => s.defaultPageTransitionDuration
  );
  const isPresentationMode = usePageStore(
    (s) => s.isPresentationMode
  );

  const isAbove40px = useScrollThreshold(40);
  const pathname = usePathname();
  const refHeaderContentMobile = useRef<HTMLDivElement>(null);
  const refBgMobileMenu = useRef<HTMLDivElement>(null);

  const menuLeft = navLeft
  const menuRight = navRight

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
  
    if (isMobileMenuOpen) {
      blockPageScroll();
      setPageTransitionDuration(0);
    } else {
      allowPageScroll();
      timeout = setTimeout(() => {
        setPageTransitionDuration(defaultPageTransitionDuration);
      }, 400);
    }
  
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isMobileMenuOpen, defaultPageTransitionDuration, setPageTransitionDuration]);

  /* Reset numMenuMobileToggles on mount to know if we have closed the menu without navigating */
  // useEffect(() => {
  //   usePageStore.getState().setNumMenuMobileToggles(0);
  // }, []);

  useGSAP(() => {
    const links =
      refHeaderContentMobile.current?.querySelectorAll('a.header-link-mobile');

    // open menu
    if (links && links.length > 0 && isMobileMenuOpen) {
      gsap.set(links, { opacity: 0, y: 20 });

      gsap.set(refBgMobileMenu.current, {
        scaleX: 0,
      });

      gsap.to(refBgMobileMenu.current, {
        duration: 0.5,
        ease: 'power2.inOut',
        scaleX: 1,
      });

      gsap.to(links, {
        duration: 0.5,
        opacity: 1,
        y: 0,
        ease: 'power2.inOut',
        stagger: { amount: 0.2 },
      });
    }
    // close menu
    else if (links && links.length > 0 && !isMobileMenuOpen) {
      gsap.to(refBgMobileMenu.current, {
        duration: 0.5,
        ease: 'power2.inOut',
        scaleX: 0,
      });

      gsap.to(links, {
        duration: 0.3,
        opacity: 0,
        y: 0,
        ease: 'power2.inOut',
        stagger: { amount: 0.1 },
      });
    }
  }, [
    isMobileMenuOpen,
    defaultPageTransitionDuration,
    setPageTransitionDuration,
  ]);

  const handleLinkClick = useCallback(
    (href: string) => () => {
      if (href === pathname) {
        setIsPageLoadFromMobileHeader(true);
      }
    },
    [pathname, setIsPageLoadFromMobileHeader]
  );

  const getClassItemActive = useCallback(
    (item: { href: string }) => {
      return pathname.startsWith(item.href) &&
        pathname !== '/' &&
        pathname !== '/es'
        ? 'link-active'
        : '';
    },
    [pathname]
  );

  const MenuItems = React.memo(function MenuItems({
    isMobileItems,
    menuLeft,
    menuRight,
    pathname,
    locale,
    currentSlugTranslations,
    getClassItemActive,
    handleLinkClick,
  }: {
    isMobileItems: boolean;
    menuLeft: any[];
    menuRight: any[];
    pathname: string;
    locale: string;
    currentSlugTranslations: Record<string, string>;
    getClassItemActive: (item: { href: string }) => string;
    handleLinkClick: (href: string) => () => void;
  }) {
    return (
    <>
      {!isPresentationMode && [menuLeft, menuRight].map((menuItems, index) => (
        <Styled.ItemsNav key={index} data-is-mobile={isMobileItems} className="header-menu">
          {menuItems.map((item, itemIndex) => (
            <React.Fragment key={itemIndex}>
              {' '}
              {item.isDynamicTranslate &&
              (pathname === '/' || pathname !== '/es') ? (
                <CustomLink
                  key={itemIndex}
                  text={locale === 'es' ? 'EN' : 'ES'}
                  href={currentSlugTranslations[locale === 'en' ? 'es' : 'en'].replace('#', '') ?? item.href}
                  className={isMobileItems ? 'header-link-mobile' : ''}
                  callbackOnClick={handleLinkClick(item.href)}
                  data-first
                  prefetch={false}
                />
              ) : (
                <CustomLink
                  key={itemIndex}
                  text={item.text}
                  href={item.href}
                  className={`${getClassItemActive(item)}${isMobileItems ? 'header-link-mobile' : ''}`}
                  callbackOnClick={handleLinkClick(item.href)}
                />
              )}
            </React.Fragment>
          ))}
        </Styled.ItemsNav>
      ))}
      {
        isPresentationMode && (
          <Styled.ItemsNav data-is-mobile={isMobileItems} className="presentation-menu">
            <CustomLink
              text={{
                en: 'Exit presentation',
                es: 'Salir de la presentación',
              }}
              href="/"
              className={`${getClassItemActive({ href: '/' })}${isMobileItems ? ' header-link-mobile' : ''}`}
              callbackOnClick={handleLinkClick('/')}
            />  
        </Styled.ItemsNav>
        )
      }
    </>
  )});

  return (
    <Styled.Header
      data-is-mobile-open={isMobileMenuOpen}
      data-is-homepage={isHomePage(pathname)}
      data-is-scrolltop={isAbove40px}
    >
      <ComponentContainer width="main" marginBottom="none" as="div">
        <TwoColContainer paddingTop="none">
          <Logo />
          <Styled.HeaderContentDesktop>
          <MenuItems
            isMobileItems={false}
            menuLeft={menuLeft}
            menuRight={menuRight}
            pathname={pathname}
            locale={locale}
            currentSlugTranslations={currentSlugTranslations}
            getClassItemActive={getClassItemActive}
            handleLinkClick={handleLinkClick}
          />
          </Styled.HeaderContentDesktop>
          <Button
            callbackOnClick={toggleMobileMenu}
            className="burger-button"
            buttonTitle={'toggle menu'}
          >
            <BurgerTwoLines isOpen={isMobileMenuOpen} />
          </Button>
        </TwoColContainer>
      </ComponentContainer>
      <Styled.HeaderContentMobile
        isOpen={isMobileMenuOpen}
        ref={refHeaderContentMobile}
      >
        <Styled.BgMobileMenu ref={refBgMobileMenu} />
        <MenuItems
            isMobileItems={true}
            menuLeft={menuLeft}
            menuRight={menuRight}
            pathname={pathname}
            locale={locale}
            currentSlugTranslations={currentSlugTranslations}
            getClassItemActive={getClassItemActive}
            handleLinkClick={handleLinkClick}
          />
      </Styled.HeaderContentMobile>
    </Styled.Header>
  );
};
